import { useEffect, useRef } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { fetchMe } from '@/store/authSlice'

/** Minimum gap between /auth/me refreshes on tab focus (ms). */
const FOCUS_THROTTLE_MS = 60_000

/**
 * Keeps `auth.user` (roles + permissions) in sync with the server without
 * requiring logout/relogin. Fires on mount and when the tab becomes visible,
 * throttled to avoid spam when switching tabs.
 */
export function AuthSessionSync() {
  const dispatch = useAppDispatch()
  const isAuthenticated = useAppSelector(s => s.auth.isAuthenticated)
  const lastFetchAt = useRef(0)

  useEffect(() => {
    if (!isAuthenticated) return

    const refresh = (force: boolean) => {
      const now = Date.now()
      if (!force && now - lastFetchAt.current < FOCUS_THROTTLE_MS) return
      lastFetchAt.current = now
      void dispatch(fetchMe())
    }

    // Boot: always refresh once when an authenticated session is present.
    refresh(true)

    const onVisibility = () => {
      if (document.visibilityState === 'visible') refresh(false)
    }
    const onFocus = () => refresh(false)

    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('focus', onFocus)
    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('focus', onFocus)
    }
  }, [dispatch, isAuthenticated])

  return null
}
